import { Drawer } from '@material-ui/core';
import {
  IconPackage,
  IconMapPin,
  IconTruckDelivery,
  IconUserCheck,
  IconShieldCheck,
  IconCalendarEvent,
  IconCreditCard,
} from '@tabler/icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { useResponsive } from '#root/hooks';
import { ShippingStatus } from '#root/models/Shipping';
import { changeCurrencySymbol, formatCurrency } from '#root/utils';
import { Button } from '#root/components/ui/primitives';
import DrawerAppbar from '../DrawerAppbar';
import ShipmentStatusBadge from '../../views/UserShipments/ShipmentStatusBadge';
import styles from './styles.module.scss';

interface ShippingDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  shipping: any;
  onOpenPayment?: (shipping: any) => void;
}

const ShippingDetailDrawer = (props: ShippingDetailDrawerProps) => {
  const { open, onClose, shipping, onOpenPayment } = props;
  const { isDesktop } = useResponsive();

  const handleClose = () => {
    onClose();
  };

  const handlePayment = () => {
    onClose();
    onOpenPayment?.(shipping);
  };

  if (!shipping) return null;

  // Status checks
  const isReady = shipping?.current_status === ShippingStatus.ready;
  const isShipped = shipping?.current_status === ShippingStatus.shipped;
  const isClosedDebited = shipping?.is_closed_debited;
  const needPayment = (isReady || isShipped || isClosedDebited) && !shipping.paid_at;

  // Pickup vs Delivery mode
  const isPickupMode = shipping?.for_pickup_at || shipping?.picked_at;

  // Pickup office info
  const pickupOfficeName = shipping?.pickup_office?.name || '---';
  const pickupOfficeAddress = shipping?.pickup_office?.address;

  // Delivery address info
  const deliveryAddress = shipping?.delivery_address;
  const hasDeliveryAddress = !!deliveryAddress?.address_1;

  // Pickup person info
  const pickupPersonName = shipping?.picked_by_name;
  const pickupPersonDoc = shipping?.picked_by_doc_type && shipping?.picked_by_doc_number
    ? `${shipping.picked_by_doc_type} ${shipping.picked_by_doc_number}`
    : null;

  // Dates
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    try {
      return format(new Date(dateStr.replace(/ /g, 'T')), "dd 'de' MMMM, yyyy", { locale: es });
    } catch {
      return null;
    }
  };

  const confirmedDate = formatDate(shipping?.confirmed_at);
  const shippedDate = formatDate(shipping?.shipped_at);
  const paidDate = formatDate(shipping?.paid_at);
  const pickedDate = formatDate(shipping?.picked_at);

  // Amount
  const currency = shipping?.currency ? changeCurrencySymbol(shipping.currency, 'Symbol') : 'S/';
  const amount = shipping?.amount;

  // Valorem (valorization chosen)
  const valorem = shipping?.valorem;

  const content = (
    <div className={styles.main}>
      {/* Status Badge */}
      <div className={styles.statusHeader}>
        <ShipmentStatusBadge
          status={shipping?.current_status}
          paid_at={shipping?.paid_at}
          shipped_at={shipping?.shipped_at}
          is_closed_debited={shipping?.is_closed_debited}
        />
      </div>

      {/* Summary Box */}
      <div className={styles.summaryBox}>
        <div className={styles.summaryIcon}>
          <IconPackage size={24} />
        </div>
        <div className={styles.summaryContent}>
          <p className={styles.summaryDescription}>{shipping?.description || '---'}</p>
          {shipping?.carrier_tracking_code && (
            <p className={styles.trackingCode}>
              Seguimiento: <strong>#{shipping.carrier_tracking_code}</strong>
            </p>
          )}
        </div>
      </div>

      {/* Valorización - sutil, solo si existe (llega como string: "516.00") */}
      {Number(valorem) > 0 && (
        <div className={styles.valoremRow}>
          <div className={styles.valoremInfo}>
            <IconShieldCheck size={16} />
            <span>Valor asegurado</span>
          </div>
          <span className={styles.valoremAmount}>{formatCurrency(valorem, 0)}</span>
        </div>
      )}



      <hr />

      {/* Modalidad de entrega */}
      {isPickupMode ? (
        // Recojo en persona
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <IconUserCheck size={20} />
            Recojo en persona
          </h4>

          {/* Lugar de recojo */}
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <IconMapPin size={18} />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Lugar de recojo</p>
              <p className={styles.infoValue}>{pickupOfficeName}</p>
              {pickupOfficeAddress && (
                <p className={styles.infoSubvalue}>{pickupOfficeAddress}</p>
              )}
            </div>
          </div>

          {/* Persona autorizada */}
          {pickupPersonName && (
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <IconUserCheck size={18} />
              </div>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Persona autorizada</p>
                <p className={styles.infoValue}>{pickupPersonName}</p>
                {pickupPersonDoc && (
                  <p className={styles.infoSubvalue}>{pickupPersonDoc}</p>
                )}
              </div>
            </div>
          )}

          {/* Fecha de recojo */}
          {pickedDate && (
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <IconCalendarEvent size={18} />
              </div>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Fecha de recojo</p>
                <p className={styles.infoValue}>{pickedDate}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Envío a domicilio
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <IconTruckDelivery size={20} />
            Envío a domicilio
          </h4>

          {/* Dirección de entrega */}
          {hasDeliveryAddress ? (
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <IconMapPin size={18} />
              </div>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Dirección de entrega</p>
                <p className={styles.infoValue}>{deliveryAddress.address_1}</p>
                <p className={styles.infoSubvalue}>
                  {deliveryAddress.district}, {deliveryAddress.province}, {deliveryAddress.department}
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.pendingInfo}>
              <p>Dirección por confirmar</p>
            </div>
          )}

          {/* Oficina de procesamiento */}
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <IconPackage size={18} />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoLabel}>Procesado por</p>
              <p className={styles.infoValue}>{pickupOfficeName}</p>
              {pickupOfficeAddress && (
                <p className={styles.infoSubvalue}>{pickupOfficeAddress}</p>
              )}
            </div>
          </div>

          {/* Amount Section - Protagonista */}
          {amount && (
            <div className={styles.amountSection}>
              <div className={styles.amountRow}>
                <span className={styles.amountLabel}>Costo total *</span>
                <span className={styles.amountValue}>{currency} {amount}</span>
              </div>
              <p className={styles.amountNote}>* Incluye embalaje, flete y seguro</p>
              {paidDate && (
                <p className={styles.paidDate}>Pagado el {paidDate}</p>
              )}
            </div>
          )}

          {/* Fecha de despacho */}
          {shippedDate && (
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <IconCalendarEvent size={18} />
              </div>
              <div className={styles.infoContent}>
                <p className={styles.infoLabel}>Fecha de despacho</p>
                <p className={styles.infoValue}>{shippedDate}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Action */}
      {needPayment && onOpenPayment && (
        <div className={styles.actions}>
          <Button
            fullWidth
            variant="primary"
            icon={<IconCreditCard size={20} />}
            onClick={handlePayment}
          >
            Pagar Envío
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            style: {
              background: 'var(--color-background-base)',
              width: 420,
              height: '100%',
            },
          }}
        >
          <DrawerAppbar title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '100%',
              background: 'var(--color-background-base)',
            },
          }}
          style={{ zIndex: 1300 }}
        >
          <DrawerAppbar isMobile title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      )}
    </>
  );
};

export default ShippingDetailDrawer;
